import React, { useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';

const emptyForm = {
  name: '',
  description: '',
  parent: '',
  isActive: true,
};

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Subcategory quick-add state inside Edit Mode
  const [quickSubName, setQuickSubName] = useState('');
  const [quickSubDesc, setQuickSubDesc] = useState('');
  const [addingQuickSub, setAddingQuickSub] = useState(false);

  // Filter & Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTab, setFilterTab] = useState('all'); // 'all' | 'parents' | 'subs'

  // Expand / Collapse state for Parent Categories (Click category to open subcategories)
  const [expandedParentIds, setExpandedParentIds] = useState({});

  const fetchCategories = () => {
    setLoading(true);
    api.get('/categories?all=true')
      .then((res) => setCategories(res.data || []))
      .catch(() => toast.error('Could not load categories'))
      .finally(() => setLoading(false));
  };

  useEffect(fetchCategories, []);

  // Compute Parent Categories (categories that have no parent or are root)
  const parentCategories = useMemo(() => {
    return categories.filter((c) => !c.parent);
  }, [categories]);

  // Compute Subcategories
  const subCategories = useMemo(() => {
    return categories.filter((c) => !c.parent ? false : true);
  }, [categories]);

  // Group subcategories by parent ID for nested tree rendering
  const subcategoriesByParent = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      if (c.parent) {
        const parentId = c.parent._id || c.parent;
        if (!map[parentId]) map[parentId] = [];
        map[parentId].push(c);
      }
    });
    return map;
  }, [categories]);

  // Toggle Category Expand/Collapse
  const toggleParentExpand = (parentId) => {
    setExpandedParentIds((prev) => ({
      ...prev,
      [parentId]: !prev[parentId],
    }));
  };

  // Expand All Categories
  const expandAll = () => {
    const all = {};
    parentCategories.forEach((p) => {
      all[p._id] = true;
    });
    setExpandedParentIds(all);
  };

  // Collapse All Categories
  const collapseAll = () => {
    setExpandedParentIds({});
  };

  // Handle Form Submit (Create or Update Category / Subcategory)
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Please provide a category name');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description ? form.description.trim() : '',
        parent: form.parent ? form.parent : null,
        isActive: form.isActive,
      };

      if (editingId) {
        await api.put(`/categories/${editingId}`, payload);
        toast.success(payload.parent ? 'Subcategory updated successfully' : 'Category updated successfully');
      } else {
        await api.post('/categories', payload);
        toast.success(payload.parent ? 'Subcategory created successfully' : 'Category created successfully');
        if (payload.parent) {
          // Auto-expand parent category when subcategory is added
          setExpandedParentIds((prev) => ({ ...prev, [payload.parent]: true }));
        }
      }
      setForm(emptyForm);
      setEditingId(null);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save category');
    } finally {
      setSaving(false);
    }
  };

  // Start Editing Category
  const edit = (c, e) => {
    if (e) e.stopPropagation();
    setEditingId(c._id);
    setForm({
      name: c.name,
      description: c.description || '',
      parent: c.parent?._id || c.parent || '',
      isActive: c.isActive !== false,
    });
    setQuickSubName('');
    setQuickSubDesc('');
    if (c.parent) {
      const pId = c.parent._id || c.parent;
      setExpandedParentIds((prev) => ({ ...prev, [pId]: true }));
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cancel Edit
  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
    setQuickSubName('');
    setQuickSubDesc('');
  };

  // Quick Add Subcategory when editing a parent category
  const handleQuickAddSubcategory = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    if (!quickSubName.trim()) {
      toast.error('Please enter subcategory name');
      return;
    }
    setAddingQuickSub(true);
    try {
      await api.post('/categories', {
        name: quickSubName.trim(),
        description: quickSubDesc ? quickSubDesc.trim() : '',
        parent: editingId,
        isActive: true,
      });
      toast.success(`Subcategory "${quickSubName.trim()}" added to ${form.name}`);
      setQuickSubName('');
      setQuickSubDesc('');
      setExpandedParentIds((prev) => ({ ...prev, [editingId]: true }));
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not add subcategory');
    } finally {
      setAddingQuickSub(false);
    }
  };

  // Quick button on a parent category row to trigger adding a subcategory under it
  const startAddSubcategoryFor = (parentCategory, e) => {
    if (e) e.stopPropagation();
    setEditingId(null);
    setForm({
      name: '',
      description: '',
      parent: parentCategory._id,
      isActive: true,
    });
    setExpandedParentIds((prev) => ({ ...prev, [parentCategory._id]: true }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Remove Category / Subcategory
  const remove = async (id, catName, e) => {
    if (e) e.stopPropagation();
    const isParent = !categories.find((c) => c._id === id)?.parent;
    const subsCount = (subcategoriesByParent[id] || []).length;
    
    if (subsCount > 0) {
      toast.error(`Cannot delete "${catName}" because it has ${subsCount} subcategories. Delete or reassign subcategories first.`);
      return;
    }

    if (!window.confirm(`Delete ${isParent ? 'category' : 'subcategory'} "${catName}"?`)) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success('Category deleted successfully');
      if (editingId === id) cancelEdit();
      fetchCategories();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not delete category');
    }
  };

  // Filter Categories for Display
  const filteredParents = parentCategories.filter((c) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    const selfMatch = c.name.toLowerCase().includes(q) || (c.description && c.description.toLowerCase().includes(q));
    const subMatch = (subcategoriesByParent[c._id] || []).some(
      (sub) => sub.name.toLowerCase().includes(q) || (sub.description && sub.description.toLowerCase().includes(q))
    );
    return selfMatch || subMatch;
  });

  // Current editing category object
  const editingCategory = editingId ? categories.find((c) => c._id === editingId) : null;
  const editingCategorySubs = editingId ? (subcategoriesByParent[editingId] || []) : [];

  return (
    <div className="space-y-6 text-[#2B1810] max-w-5xl">
      {/* Header Banner */}
      <div className="bg-white border border-[#E8DFD8] rounded-2xl p-6 shadow-sm flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#C49A45]" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#9D772D]">Catalog Hierarchy</span>
          </div>
          <h1 className="font-heading text-2xl font-bold text-[#2B1810] mt-0.5">Categories & Subcategories</h1>
          <p className="text-xs text-[#8A796C]">Click any main category in the list below to expand and manage its subcategories.</p>
        </div>

        {/* Stats Summary Badges */}
        <div className="flex items-center gap-2.5">
          <div className="px-3.5 py-2 bg-[#FAF8F5] border border-[#E8DFD8] rounded-xl text-center">
            <span className="block text-base font-bold font-heading text-[#2B1810] leading-none">{categories.length}</span>
            <span className="text-[9px] font-bold text-[#8A796C] uppercase tracking-wider">Total</span>
          </div>
          <div className="px-3.5 py-2 bg-[#FAF8F5] border border-[#E8DFD8] rounded-xl text-center">
            <span className="block text-base font-bold font-heading text-[#C49A45] leading-none">{parentCategories.length}</span>
            <span className="text-[9px] font-bold text-[#8A796C] uppercase tracking-wider">Main</span>
          </div>
          <div className="px-3.5 py-2 bg-[#FAF8F5] border border-[#E8DFD8] rounded-xl text-center">
            <span className="block text-base font-bold font-heading text-emerald-700 leading-none">{subCategories.length}</span>
            <span className="text-[9px] font-bold text-[#8A796C] uppercase tracking-wider">Subcategories</span>
          </div>
        </div>
      </div>

      {/* Main Category Form (Create / Edit with Subcategory Parent Selection) */}
      <form onSubmit={submit} className="bg-white border border-[#E8DFD8] rounded-2xl p-6 shadow-sm space-y-5">
        <div className="flex items-center justify-between border-b border-[#E8DFD8] pb-3.5">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-amber-50 text-[#8C6B28] flex items-center justify-center font-bold text-xs border border-[#C49A45]/30">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
            </span>
            <div>
              <h3 className="font-heading text-base font-bold text-[#2B1810]">
                {editingId
                  ? `Edit ${form.parent ? 'Subcategory' : 'Category'}: ${editingCategory?.name || ''}`
                  : form.parent
                  ? `Create Subcategory under "${parentCategories.find((p) => p._id === form.parent)?.name || ''}"`
                  : 'Create New Category or Subcategory'}
              </h3>
              <p className="text-[11px] text-[#8A796C]">
                {form.parent
                  ? 'This item will be created as a subcategory nested under the selected parent.'
                  : 'Select a parent category below to make this a subcategory, or leave as None for a top-level category.'}
              </p>
            </div>
          </div>

          {editingId && (
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-300">
              Editing Mode
            </span>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {/* Category Name */}
          <div>
            <label className="label">
              {form.parent ? 'Subcategory Name' : 'Category Name'} <span className="text-red-500">*</span>
            </label>
            <input
              required
              className="input-field"
              placeholder={form.parent ? 'e.g. Ganesha Idols, 108 Beads Mala' : 'e.g. Pure Sandalwood Idols'}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          {/* Parent Category Selector */}
          <div>
            <label className="label">
              Parent Category <span className="text-[#8A796C] font-normal lowercase">(optional for subcategory)</span>
            </label>
            <select
              className="input-field font-medium"
              value={form.parent}
              onChange={(e) => setForm({ ...form, parent: e.target.value })}
            >
              <option value="">None (Top-Level Main Category)</option>
              <optgroup label="Attach as Subcategory under:">
                {parentCategories
                  .filter((p) => p._id !== editingId)
                  .map((p) => (
                    <option key={p._id} value={p._id}>
                      ↳ {p.name}
                    </option>
                  ))}
              </optgroup>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="label">Description (Optional)</label>
            <input
              className="input-field"
              placeholder="e.g. Handcrafted sacred sandalwood artifacts"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-2 border-t border-[#E8DFD8]">
          <label className="flex items-center gap-2 text-xs font-bold text-[#2B1810] cursor-pointer">
            <input
              type="checkbox"
              className="accent-[#C49A45] w-4 h-4 rounded"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            <span>Active & Visible in Store</span>
          </label>

          <div className="flex items-center gap-3">
            {editingId && (
              <button
                type="button"
                className="bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#D9D0C8] text-[#2B1810] font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-lg transition-colors"
                onClick={cancelEdit}
              >
                Cancel Edit
              </button>
            )}

            <button
              type="submit"
              disabled={saving}
              className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-7 py-2.5 rounded-lg shadow-md transition-colors inline-flex items-center gap-1.5"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              <span>{saving ? 'Saving...' : editingId ? 'Update Category' : form.parent ? 'Create Subcategory' : 'Create Category'}</span>
            </button>
          </div>
        </div>

        {/* Dedicated Subcategories Manager Box when Editing a Main Category */}
        {editingId && !editingCategory?.parent && (
          <div className="mt-4 bg-[#FAF8F5] border border-[#E8DFD8] rounded-xl p-4.5 space-y-3.5">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-md bg-[#C49A45]/20 text-[#8C6B28] flex items-center justify-center font-bold text-xs">
                  ↳
                </span>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#2B1810]">
                    Subcategories under "{editingCategory?.name}" ({editingCategorySubs.length})
                  </h4>
                  <p className="text-[10px] text-[#8A796C]">
                    Manage subcategories directly attached to this parent category.
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Add Subcategory Inline Form */}
            <div className="bg-white p-3 rounded-lg border border-[#E8DFD8] flex flex-col sm:flex-row items-center gap-2">
              <input
                type="text"
                placeholder="New subcategory name (e.g. Shiva Idols)..."
                className="input-field text-xs py-1.5 flex-1"
                value={quickSubName}
                onChange={(e) => setQuickSubName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleQuickAddSubcategory(e);
                  }
                }}
              />
              <input
                type="text"
                placeholder="Description (optional)..."
                className="input-field text-xs py-1.5 flex-1"
                value={quickSubDesc}
                onChange={(e) => setQuickSubDesc(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleQuickAddSubcategory(e);
                  }
                }}
              />
              <button
                type="button"
                onClick={handleQuickAddSubcategory}
                disabled={addingQuickSub || !quickSubName.trim()}
                className="w-full sm:w-auto px-4 py-2 bg-[#2B1810] hover:bg-[#1C100A] disabled:opacity-50 text-white font-bold text-xs uppercase tracking-wider rounded-md transition-colors shrink-0 inline-flex items-center justify-center gap-1"
              >
                <span>+ Add Subcategory</span>
              </button>
            </div>

            {/* List of existing subcategories under this parent */}
            {editingCategorySubs.length === 0 ? (
              <p className="text-xs text-[#8A796C] italic bg-white p-3 rounded-lg border border-dashed border-[#D9D0C8] text-center">
                No subcategories created yet under "{editingCategory?.name}". Use the input above to add the first one!
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                {editingCategorySubs.map((sub) => (
                  <div
                    key={sub._id}
                    className="p-2.5 bg-white border border-[#E8DFD8] rounded-lg flex items-center justify-between gap-2 shadow-2xs hover:border-[#C49A45] transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-[#2B1810] truncate">↳ {sub.name}</p>
                      {sub.description && (
                        <p className="text-[10px] text-[#8A796C] truncate mt-0.5">{sub.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={(e) => edit(sub, e)}
                        className="px-2 py-1 text-[10px] font-bold uppercase rounded bg-[#FAF8F5] border border-[#D9D0C8] hover:border-[#C49A45] text-[#2B1810] transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={(e) => remove(sub._id, sub.name, e)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-rose-50 rounded transition-colors"
                        title={`Delete subcategory ${sub.name}`}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </form>

      {/* Filter Tabs, Expand/Collapse & Search Bar */}
      <div className="bg-white border border-[#E8DFD8] rounded-2xl p-4 shadow-sm space-y-3">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Tabs */}
          <div className="flex items-center bg-[#FAF8F5] p-1 rounded-xl border border-[#E8DFD8] w-full sm:w-auto flex-wrap">
            <button
              type="button"
              onClick={() => setFilterTab('all')}
              className={`flex-1 sm:flex-initial px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                filterTab === 'all'
                  ? 'bg-[#C49A45] text-white shadow-xs'
                  : 'text-[#8A796C] hover:text-[#2B1810]'
              }`}
            >
              All Categories ({categories.length})
            </button>
            <button
              type="button"
              onClick={() => setFilterTab('parents')}
              className={`flex-1 sm:flex-initial px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                filterTab === 'parents'
                  ? 'bg-[#C49A45] text-white shadow-xs'
                  : 'text-[#8A796C] hover:text-[#2B1810]'
              }`}
            >
              Main Parents ({parentCategories.length})
            </button>
            <button
              type="button"
              onClick={() => setFilterTab('subs')}
              className={`flex-1 sm:flex-initial px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all ${
                filterTab === 'subs'
                  ? 'bg-[#C49A45] text-white shadow-xs'
                  : 'text-[#8A796C] hover:text-[#2B1810]'
              }`}
            >
              Subcategories ({subCategories.length})
            </button>
          </div>

          {/* Quick Expand/Collapse All + Search Bar */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={expandAll}
              className="px-2.5 py-1.5 text-[11px] font-bold text-[#8C6B28] hover:text-[#2B1810] bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#D9D0C8] rounded-lg transition-colors whitespace-nowrap"
              title="Open all subcategories"
            >
              Expand All
            </button>
            <button
              type="button"
              onClick={collapseAll}
              className="px-2.5 py-1.5 text-[11px] font-bold text-[#8A796C] hover:text-[#2B1810] bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#D9D0C8] rounded-lg transition-colors whitespace-nowrap"
              title="Close all subcategories"
            >
              Collapse All
            </button>

            <div className="relative flex-1 sm:w-64">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search category or subcategory..."
                className="w-full bg-[#FAF8F5] border border-[#D9D0C8] rounded-xl px-3.5 py-1.5 text-xs text-[#2B1810] placeholder-[#8A796C] pr-8 focus:outline-none focus:border-[#C49A45]"
              />
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#8A796C] hover:text-[#2B1810] text-xs"
                >
                  ✕
                </button>
              ) : (
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#8A796C] pointer-events-none">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Hierarchical Categories & Subcategories List with Click to Open Subcategories */}
        <div className="divide-y divide-[#E8DFD8] pt-2">
          {loading ? (
            <div className="p-8 text-center text-xs text-[#8A796C]">Loading categories...</div>
          ) : filterTab === 'subs' ? (
            // Flat list of subcategories
            subCategories.length === 0 ? (
              <div className="p-8 text-center text-xs text-[#8A796C]">No subcategories found.</div>
            ) : (
              subCategories
                .filter((sub) => {
                  const q = searchQuery.toLowerCase().trim();
                  if (!q) return true;
                  return sub.name.toLowerCase().includes(q) || (sub.description && sub.description.toLowerCase().includes(q));
                })
                .map((sub) => (
                  <div key={sub._id} className="p-3.5 flex items-center justify-between hover:bg-[#FAF8F5] transition-colors rounded-xl">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-xs">
                          ↳
                        </span>
                        <h4 className="font-bold text-sm text-[#2B1810]">{sub.name}</h4>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-[#8C6B28] border border-[#C49A45]/30">
                          Parent: {sub.parent?.name || 'Main Category'}
                        </span>
                      </div>
                      {sub.description && <p className="text-xs text-[#8A796C] mt-1 ml-7">{sub.description}</p>}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={(e) => edit(sub, e)}
                        className="bg-white hover:bg-[#F5EFEB] border border-[#D9D0C8] text-[#2B1810] font-bold text-xs py-1.5 px-3 rounded-lg transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={(e) => remove(sub._id, sub.name, e)}
                        className="bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs py-1.5 px-3 rounded-lg transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
            )
          ) : filteredParents.length === 0 ? (
            <div className="p-8 text-center text-xs text-[#8A796C]">
              {searchQuery ? `No categories found matching "${searchQuery}".` : 'No categories created yet.'}
            </div>
          ) : (
            // Hierarchical Tree View with Click to Open/Close Subcategories
            filteredParents.map((parent) => {
              const subs = subcategoriesByParent[parent._id] || [];
              const isExpanded = expandedParentIds[parent._id];

              return (
                <div key={parent._id} className="py-3 space-y-2">
                  {/* Top-Level Main Category Card (Click anywhere to Open Subcategories) */}
                  <div
                    onClick={() => {
                      if (subs.length > 0) toggleParentExpand(parent._id);
                    }}
                    className={`p-4 rounded-xl border transition-all flex items-center justify-between gap-3 select-none ${
                      subs.length > 0 ? 'cursor-pointer' : 'cursor-default'
                    } ${
                      editingId === parent._id
                        ? 'bg-amber-50/60 border-[#C49A45] ring-1 ring-[#C49A45]'
                        : isExpanded
                        ? 'bg-white border-[#C49A45]/80 shadow-xs'
                        : 'bg-[#FAF8F5]/80 hover:bg-[#FAF8F5] border-[#E8DFD8]'
                    }`}
                  >
                    <div className="min-w-0 flex-1 flex items-center gap-3">
                      {/* Chevron Arrow Toggle */}
                      {subs.length > 0 ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleParentExpand(parent._id);
                          }}
                          className={`w-7 h-7 rounded-lg bg-white border border-[#D9D0C8] text-[#8C6B28] flex items-center justify-center transition-transform duration-200 shrink-0 ${
                            isExpanded ? 'rotate-90 bg-amber-50 border-[#C49A45]' : 'hover:border-[#C49A45]'
                          }`}
                          title={isExpanded ? 'Click to close subcategories' : 'Click to open subcategories'}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>
                      ) : (
                        <span className="w-7 h-7 rounded-lg bg-[#FAF8F5] text-[#8A796C] flex items-center justify-center font-bold text-xs shrink-0 border border-[#E8DFD8]">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
                        </span>
                      )}

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <h4 className="font-heading font-bold text-sm sm:text-base text-[#2B1810] truncate">
                            {parent.name}
                          </h4>

                          {/* Subcategory Count Badge (Clickable) */}
                          <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full flex items-center gap-1 transition-colors ${
                            subs.length > 0
                              ? isExpanded
                                ? 'bg-[#C49A45] text-white shadow-2xs'
                                : 'bg-[#C49A45]/15 text-[#8C6B28] border border-[#C49A45]/30'
                              : 'bg-gray-100 text-[#8A796C]'
                          }`}>
                            <span>{subs.length} {subs.length === 1 ? 'Subcategory' : 'Subcategories'}</span>
                            {subs.length > 0 && (
                              <span className="text-[9px] opacity-80">{isExpanded ? '▲ Open' : '▼ Click to Open'}</span>
                            )}
                          </span>

                          {parent.isActive === false && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-800">
                              Hidden
                            </span>
                          )}
                        </div>

                        {parent.description && (
                          <p className="text-xs text-[#8A796C] mt-0.5 truncate">{parent.description}</p>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons on Parent */}
                    <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => startAddSubcategoryFor(parent, e)}
                        className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs py-1.5 px-3 rounded-lg transition-colors shadow-2xs inline-flex items-center gap-1"
                        title={`Add subcategory under ${parent.name}`}
                      >
                        <span className="text-sm leading-none">+</span>
                        <span className="hidden sm:inline">Subcategory</span>
                      </button>
                      <button
                        type="button"
                        onClick={(e) => edit(parent, e)}
                        className="bg-white hover:bg-[#F5EFEB] border border-[#D9D0C8] text-[#2B1810] font-bold text-xs py-1.5 px-3 rounded-lg transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={(e) => remove(parent._id, parent.name, e)}
                        className="bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs py-1.5 px-3 rounded-lg transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Nested Subcategories List (Revealed when Category is Clicked/Expanded) */}
                  {subs.length > 0 && isExpanded && (
                    <div className="ml-5 sm:ml-9 space-y-1.5 border-l-2 border-[#C49A45] pl-3 sm:pl-4 animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-[#8C6B28] py-0.5">
                        Subcategories under "{parent.name}":
                      </div>
                      {subs.map((sub) => (
                        <div
                          key={sub._id}
                          className={`p-3 rounded-xl border flex items-center justify-between gap-3 transition-colors ${
                            editingId === sub._id
                              ? 'bg-amber-50/60 border-[#C49A45] ring-1 ring-[#C49A45]'
                              : 'bg-white hover:bg-[#FAF8F5] border-[#E8DFD8]'
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[#C49A45] font-bold text-xs">↳</span>
                              <h5 className="font-bold text-xs sm:text-sm text-[#2B1810] truncate">
                                {sub.name}
                              </h5>
                              <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                                Subcategory
                              </span>
                            </div>
                            {sub.description && (
                              <p className="text-[11px] text-[#8A796C] mt-0.5 ml-4 truncate">
                                {sub.description}
                              </p>
                            )}
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={(e) => edit(sub, e)}
                              className="bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#D9D0C8] text-[#2B1810] font-bold text-[11px] py-1 px-2.5 rounded-lg transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={(e) => remove(sub._id, sub.name, e)}
                              className="bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-[11px] py-1 px-2 rounded-lg transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
